import React from 'react';
import { IonIcon } from '@ionic/react';
import { walletOutline } from 'ionicons/icons';
import {
  PendingItems,
  WalletStatusCardData,
} from '../../../../types/wallet/Walletkyc';
import useWalletCreation from '../../../../services/walletSetup/hooks/useWalletCreation';

type Props = WalletStatusCardData | null;

const formatPendingItem = (item: PendingItems) =>
  item
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const WalletKycProgressCard: React.FC<Props> = (props) => {
  const { requestNewWallet } = useWalletCreation();

  if (!props) return null;

  const canCreateWallet =
    props.overall_status === 'verified' &&
    props.wallet_status === 'not_created';
  const isWalletCreationPending =
    props.overall_status === 'verified' &&
    ['pending', 'Pending'].includes(props.wallet_status);
  const isRejected = props.overall_status === 'rejected';
  const pendingItems = props.pending_items || [];

  return (
    <div className="p-4 sm:p-6 min-h-56 h-full bg-background dark:bg-background-accent border border-border rounded-lg overflow-hidden">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-text-dark-high dark:text-text-light-high">
          My Wallet
        </h2>
      </div>

      <div className="before:w-[calc(100%-2rem)] before:top-4 mb-8 before:-translate-x-1/2 before:left-1/2 before:h-full before:absolute relative before:rounded-lg before:z-[1] before:bg-customs-lavender-foreground before:dark:bg-background-accent before:border before:border-secondary">
        <div className="p-4 bg-customs-lavender-foreground dark:bg-background-accent border border-secondary rounded-lg relative z-[3] space-y-4">
          <div className="bg-white p-2 rounded-lg shadow-sm border border-secondary text-primary flex items-center justify-center w-fit mx-auto">
            <IonIcon icon={walletOutline} className="size-12" />
          </div>

          <div className="flex flex-col justify-between gap-2 items-center mb-3">
            <h3 className="text-lg font-semibold high-emphasis-text">
              {isRejected && 'Verification Needs Attention'}
              {canCreateWallet && 'KYC Verified Successfully'}
              {isWalletCreationPending && 'Wallet Creation in Progress'}
              {!isRejected &&
                !canCreateWallet &&
                !isWalletCreationPending &&
                `${props.entity_type === 'corporate' ? 'Corporate' : 'Wallet'} Verification in Progress`}
            </h3>
            <p className="text-center text-sm medium-emphasis-text">
              {isRejected &&
                "We couldn't complete your verification. Please review the outstanding requirements and try again."}
              {canCreateWallet && "You're all set. Create your wallet now."}
              {isWalletCreationPending &&
                'Your wallet request has been received and is being processed.'}
              {!isRejected &&
                !canCreateWallet &&
                !isWalletCreationPending &&
                'We are still waiting on the remaining verification requirements from the backend before a wallet can be created.'}
            </p>
            {pendingItems.length > 0 && (
              <div className="flex flex-wrap justify-center gap-2">
                {pendingItems.map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-secondary bg-white px-3 py-1 text-xs medium-emphasis-text"
                  >
                    {formatPendingItem(item)}
                  </span>
                ))}
              </div>
            )}
            {canCreateWallet && (
              <button
                className="flex-1 disabled:hover:cursor-default flex items-center justify-center gap-2 p-2  bg-white border border-gray-300 rounded-lg text-sm text-text-dark-high font-medium hover:bg-gray-50 hover:cursor-pointer transition"
                onClick={() => requestNewWallet(props.customer_id)}
              >
                Create Wallet
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WalletKycProgressCard;
